import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Wand2, 
  Check, 
  ChevronRight, 
  ChevronLeft,
  ExternalLink,
  Copy,
  Loader2,
  AlertCircle,
  PartyPopper
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/client';
import Logo from '../components/shared/Logo';

type Step = 'welcome' | 'microsoft' | 'google' | 'complete';

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<Step>('welcome');
  
  const [microsoftConfig, setMicrosoftConfig] = useState({
    client_id: '',
    client_secret: '',
  });
  
  const [googleConfig, setGoogleConfig] = useState({
    client_id: '',
    client_secret: '',
  });
  
  const [configuredProviders, setConfiguredProviders] = useState<{
    microsoft: boolean;
    google: boolean;
  }>({ microsoft: false, google: false });
  
  const mutation = useMutation({
    mutationFn: adminApi.updateOAuthSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauth-status'] });
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });
  
  const baseUrl = window.location.origin;
  const microsoftRedirectUri = `${baseUrl}/api/auth/callback/microsoft`;
  const googleRedirectUri = `${baseUrl}/api/auth/callback/google`;
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopiert!');
  };
  
  const saveMicrosoft = async () => {
    if (!microsoftConfig.client_id || !microsoftConfig.client_secret) {
      // Skip this step
      setCurrentStep('google');
      return;
    }
    
    await mutation.mutateAsync({
      microsoft_client_id: microsoftConfig.client_id,
      microsoft_client_secret: microsoftConfig.client_secret,
    });
    
    setConfiguredProviders(prev => ({ ...prev, microsoft: true }));
    toast.success('Microsoft OAuth konfiguriert!');
    setCurrentStep('google');
  };
  
  const saveGoogle = async () => {
    if (!googleConfig.client_id || !googleConfig.client_secret) {
      // Skip this step
      if (!configuredProviders.microsoft && !configuredProviders.google) {
        toast.error('Mindestens ein OAuth-Provider muss konfiguriert werden');
        return;
      }
      setCurrentStep('complete');
      return;
    }
    
    await mutation.mutateAsync({
      google_client_id: googleConfig.client_id,
      google_client_secret: googleConfig.client_secret,
    });
    
    setConfiguredProviders(prev => ({ ...prev, google: true }));
    toast.success('Google OAuth konfiguriert!');
    setCurrentStep('complete');
  };
  
  const canProceedFromGoogle = () => {
    return configuredProviders.microsoft || 
           (googleConfig.client_id && googleConfig.client_secret);
  };
  
  const finishSetup = () => {
    navigate('/dashboard');
  };
  
  return (
    <div className="min-h-screen bg-bg-base flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Logo className="h-10 mx-auto mb-4" />
          <h1 className="text-h1 text-text-primary mb-2">
            Willkommen bei Kolumi
          </h1>
          <p className="text-text-muted">
            Lass uns die App einrichten – dauert nur 2 Minuten!
          </p>
        </div>
        
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['welcome', 'microsoft', 'google', 'complete'].map((step, index) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep === step
                    ? 'bg-primary text-white'
                    : index < ['welcome', 'microsoft', 'google', 'complete'].indexOf(currentStep)
                    ? 'bg-green-500 text-white'
                    : 'bg-bg-surface text-text-muted'
                }`}
              >
                {index < ['welcome', 'microsoft', 'google', 'complete'].indexOf(currentStep) ? (
                  <Check className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </div>
              {index < 3 && (
                <div className={`w-8 h-0.5 mx-1 ${
                  index < ['welcome', 'microsoft', 'google', 'complete'].indexOf(currentStep)
                    ? 'bg-green-500'
                    : 'bg-bg-surface'
                }`} />
              )}
            </div>
          ))}
        </div>
        
        {/* Content Card */}
        <div className="bg-bg-elevated rounded-xl border border-border-default p-6">
          {/* Welcome Step */}
          {currentStep === 'welcome' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Wand2 className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary mb-2">
                  Setup-Assistent
                </h2>
                <p className="text-text-muted">
                  Um Kolumi nutzen zu können, musst du mindestens einen OAuth-Provider 
                  (Microsoft 365 oder Google) einrichten. So können Benutzer ihre 
                  E-Mail-Konten verbinden.
                </p>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-left">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-text-secondary">
                  <p className="font-medium text-text-primary mb-1">Was du brauchst:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Zugang zu Azure Portal (für Microsoft 365)</li>
                    <li>Zugang zu Google Cloud Console (für Gmail)</li>
                    <li>Ca. 5-10 Minuten Zeit</li>
                  </ul>
                </div>
              </div>
              <button
                onClick={() => setCurrentStep('microsoft')}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary text-white font-medium hover:bg-primary/90"
              >
                Los geht's
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Microsoft Step */}
          {currentStep === 'microsoft' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <svg className="w-8 h-8" viewBox="0 0 21 21" fill="currentColor">
                  <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
                </svg>
                <div>
                  <h2 className="text-xl font-semibold text-text-primary">
                    Microsoft 365 / Outlook
                  </h2>
                  <p className="text-sm text-text-muted">Optional – kann übersprungen werden</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-bg-surface space-y-2">
                  <p className="text-sm text-text-secondary">
                    <strong>Schritt 1:</strong> Öffne das{' '}
                    <a 
                      href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      Azure Portal <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                  <p className="text-sm text-text-secondary">
                    <strong>Schritt 2:</strong> Erstelle eine neue App-Registrierung
                  </p>
                  <p className="text-sm text-text-secondary">
                    <strong>Schritt 3:</strong> Füge diese Redirect URI hinzu:
                  </p>
                  <div className="flex gap-2">
                    <code className="flex-1 px-3 py-2 rounded bg-bg-base text-sm text-text-muted overflow-x-auto">
                      {microsoftRedirectUri}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(microsoftRedirectUri)}
                      className="px-3 py-2 rounded bg-bg-base text-text-secondary hover:bg-bg-surface-hover"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Application (Client) ID
                  </label>
                  <input
                    type="text"
                    value={microsoftConfig.client_id}
                    onChange={(e) => setMicrosoftConfig(prev => ({ ...prev, client_id: e.target.value }))}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-3 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Client Secret
                  </label>
                  <input
                    type="password"
                    value={microsoftConfig.client_secret}
                    onChange={(e) => setMicrosoftConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                    placeholder="Aus Azure Portal kopieren"
                    className="w-full px-3 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep('welcome')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-text-muted hover:text-text-secondary"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Zurück
                </button>
                <button
                  onClick={saveMicrosoft}
                  disabled={mutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {mutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : microsoftConfig.client_id && microsoftConfig.client_secret ? (
                    <>
                      Speichern & Weiter
                      <ChevronRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Überspringen
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Google Step */}
          {currentStep === 'google' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <svg className="w-8 h-8" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <div>
                  <h2 className="text-xl font-semibold text-text-primary">
                    Google / Gmail
                  </h2>
                  <p className="text-sm text-text-muted">
                    {configuredProviders.microsoft 
                      ? 'Optional – kann übersprungen werden' 
                      : 'Erforderlich wenn Microsoft nicht konfiguriert'}
                  </p>
                </div>
              </div>
              
              {configuredProviders.microsoft && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-500">Microsoft ist bereits konfiguriert</span>
                </div>
              )}
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-bg-surface space-y-2">
                  <p className="text-sm text-text-secondary">
                    <strong>Schritt 1:</strong> Öffne die{' '}
                    <a 
                      href="https://console.cloud.google.com/apis/credentials" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      Google Cloud Console <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                  <p className="text-sm text-text-secondary">
                    <strong>Schritt 2:</strong> Erstelle OAuth 2.0 Credentials (Web Application)
                  </p>
                  <p className="text-sm text-text-secondary">
                    <strong>Schritt 3:</strong> Füge diese Redirect URI hinzu:
                  </p>
                  <div className="flex gap-2">
                    <code className="flex-1 px-3 py-2 rounded bg-bg-base text-sm text-text-muted overflow-x-auto">
                      {googleRedirectUri}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(googleRedirectUri)}
                      className="px-3 py-2 rounded bg-bg-base text-text-secondary hover:bg-bg-surface-hover"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={googleConfig.client_id}
                    onChange={(e) => setGoogleConfig(prev => ({ ...prev, client_id: e.target.value }))}
                    placeholder="xxxxx-xxxxx.apps.googleusercontent.com"
                    className="w-full px-3 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Client Secret
                  </label>
                  <input
                    type="password"
                    value={googleConfig.client_secret}
                    onChange={(e) => setGoogleConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                    placeholder="Aus Google Console kopieren"
                    className="w-full px-3 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep('microsoft')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-text-muted hover:text-text-secondary"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Zurück
                </button>
                <button
                  onClick={saveGoogle}
                  disabled={mutation.isPending || !canProceedFromGoogle()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {mutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : googleConfig.client_id && googleConfig.client_secret ? (
                    <>
                      Speichern & Weiter
                      <ChevronRight className="w-4 h-4" />
                    </>
                  ) : configuredProviders.microsoft ? (
                    <>
                      Überspringen
                      <ChevronRight className="w-4 h-4" />
                    </>
                  ) : (
                    'Google muss konfiguriert werden'
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Complete Step */}
          {currentStep === 'complete' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                <PartyPopper className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary mb-2">
                  Setup abgeschlossen!
                </h2>
                <p className="text-text-muted">
                  Kolumi ist jetzt einsatzbereit. Benutzer können sich registrieren 
                  und ihre E-Mail-Konten verbinden.
                </p>
              </div>
              
              <div className="space-y-2 text-left">
                <p className="text-sm font-medium text-text-secondary">Konfigurierte Provider:</p>
                <div className="flex gap-4 justify-center">
                  {configuredProviders.microsoft && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-500">Microsoft 365</span>
                    </div>
                  )}
                  {configuredProviders.google && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-500">Google</span>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={finishSetup}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary text-white font-medium hover:bg-primary/90"
              >
                Zum Dashboard
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
